"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { useWaveTopVoters } from "@/hooks/useWaveTopVoters";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";
import { SingleWaveDropVoter } from "./SingleWaveDropVoter";
interface SingleWaveDropVotersProps {
  readonly drop: ApiDrop;
}
export const SingleWaveDropVoters: React.FC<SingleWaveDropVotersProps> = t0 => {
  const $ = _c(39);
  const {
    drop
  } = t0;
  const {
    connectedProfile
  } = useAuth();
  const [isVotersOpen, setIsVotersOpen] = useState(false);
  const t1 = connectedProfile?.handle ?? undefined;
  let t2;
  if ($[0] !== drop.id || $[1] !== drop.wave.id || $[2] !== isVotersOpen || $[3] !== t1) {
    t2 = {
      waveId: drop.wave.id,
      connectedProfileHandle: t1,
      reverse: false,
      dropId: drop.id,
      sortDirection: "DESC",
      sort: "ABSOLUTE",
      enabled: isVotersOpen
    };
    $[0] = drop.id;
    $[1] = drop.wave.id;
    $[2] = isVotersOpen;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    voters,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isLoading
  } = useWaveTopVoters(t2);
  let t3;
  if ($[5] !== fetchNextPage || $[6] !== hasNextPage || $[7] !== isFetchingNextPage || $[8] !== isLoading) {
    t3 = () => {
      if (hasNextPage && !isLoading && !isFetchingNextPage) {
        fetchNextPage();
      }
    };
    $[5] = fetchNextPage;
    $[6] = hasNextPage;
    $[7] = isFetchingNextPage;
    $[8] = isLoading;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const intersectionElementRef = useIntersectionObserver(t3);
  let t4;
  if ($[10] !== isVotersOpen) {
    t4 = () => setIsVotersOpen(!isVotersOpen);
    $[10] = isVotersOpen;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = `tw-flex tw-w-full tw-items-center tw-justify-between tw-border-0 tw-px-4 tw-py-4 tw-text-left tw-transition-colors tw-duration-300 tw-ease-out desktop-hover:hover:tw-bg-iron-900 ${isVotersOpen ? "tw-bg-iron-800" : "tw-bg-iron-950"}`;
  const t6 = `tw-text-sm tw-font-medium ${isVotersOpen ? "tw-text-iron-300" : "tw-text-iron-400"}`;
  let t7;
  if ($[12] !== t6) {
    t7 = <span className={t6}>Top voters</span>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = isVotersOpen ? 180 : 0;
  let t9;
  if ($[14] !== t8) {
    t9 = {
      rotate: t8
    };
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      duration: 0.3
    };
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = `tw-h-4 tw-w-4 tw-flex-shrink-0 ${isVotersOpen ? "tw-text-iron-400" : "tw-text-iron-600"}`;
  let t12;
  if ($[17] !== t11) {
    t12 = <ChevronDownIcon className={t11} />;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t12 || $[20] !== t9) {
    t13 = <motion.div animate={t9} transition={t10}>{t12}</motion.div>;
    $[19] = t12;
    $[20] = t9;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13 || $[23] !== t4 || $[24] !== t5 || $[25] !== t7) {
    t14 = <button onClick={t4} className={t5}>{t7}{t13}</button>;
    $[22] = t13;
    $[23] = t4;
    $[24] = t5;
    $[25] = t7;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== drop.wave.voting_credit_type || $[28] !== intersectionElementRef || $[29] !== isFetchingNextPage || $[30] !== isLoading || $[31] !== isVotersOpen || $[32] !== voters) {
    t15 = isVotersOpen && <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: "auto",
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="tw-overflow-hidden"><div className="tw-max-h-[19.75rem] tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300">{voters.length > 0 || isLoading ? <><div className="tw-divide-x-0 tw-divide-y tw-divide-solid tw-divide-iron-800">{voters.map((voter, index) => <SingleWaveDropVoter voter={voter} key={voter.voter.id} position={index + 1} creditType={drop.wave.voting_credit_type} />)}</div>{(isFetchingNextPage || isLoading) && <div className="tw-h-0.5 tw-w-full tw-overflow-hidden tw-bg-iron-800"><div className="tw-h-full tw-w-full tw-animate-loading-bar tw-bg-indigo-400" /></div>}<div ref={intersectionElementRef} /></> : <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-space-y-4 tw-py-6 tw-text-iron-400"><div className="tw-group tw-relative"><div className="tw-absolute tw-inset-0 tw-animate-[spin_4s_linear_infinite] tw-rounded-full tw-bg-gradient-to-br tw-from-primary-400/20 tw-via-indigo-500/10 tw-to-iron-800/10 group-hover:tw-from-primary-400/30" /><div className="tw-absolute tw-inset-0 tw-animate-[spin_5s_linear_infinite] tw-rounded-full tw-bg-gradient-to-tr tw-from-iron-800/10 tw-via-indigo-500/10 tw-to-primary-400/20 group-hover:tw-to-primary-400/30" /><div className="tw-bg-gradient-radial tw-absolute tw-inset-0 tw-animate-pulse tw-from-primary-300/5 tw-to-transparent" /><svg className="tw-relative tw-size-8 tw-flex-shrink-0 tw-text-white/60" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M22.7 13.5L20.7005 11.5L18.7 13.5M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C15.3019 3 18.1885 4.77814 19.7545 7.42909M12 7V12L15 14" /></svg></div><div className="tw-flex tw-flex-col tw-items-center tw-gap-y-2"><span className="tw-bg-gradient-to-br tw-from-iron-200 tw-via-iron-300 tw-to-iron-400 tw-bg-clip-text tw-text-base tw-font-semibold tw-tracking-tight tw-text-transparent">Be the First to Make a Vote</span><p className="tw-mb-0 tw-max-w-64 tw-text-center tw-text-sm tw-text-iron-500">Vote on this drop to see voter rankings appear here.</p></div></div>}</div></motion.div>;
    $[27] = drop.wave.voting_credit_type;
    $[28] = intersectionElementRef;
    $[29] = isFetchingNextPage;
    $[30] = isLoading;
    $[31] = isVotersOpen;
    $[32] = voters;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t15) {
    t16 = <AnimatePresence>{t15}</AnimatePresence>;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t14 || $[37] !== t16) {
    t17 = <div>{t14}{t16}</div>;
    $[36] = t14;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  return t17;
};